export const BULK_EXERCISE_HEADERS = [
  "exercise_name",
  "video_url",
  "youtube_video_id",
  "condition",
  "phase",
  "level",
  "sets",
  "reps",
  "hold",
  "frequency",
  "equipment",
  "playlist",
  "description",
  "instructions",
  "thumbnail_url",
  "safety_notes",
  "is_active",
];

const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

export function extractYoutubeVideoId(value) {
  const rawUrl = String(value ?? "").trim();
  if (!rawUrl) return "";
  try {
    const url = new URL(rawUrl);
    if (!['http:', 'https:'].includes(url.protocol)) return "";
    const hostname = url.hostname.toLowerCase().replace(/^www\./, "").replace(/^m\./, "");
    let videoId = "";
    if (hostname === "youtu.be") {
      videoId = url.pathname.split("/").filter(Boolean)[0] || "";
    } else if (hostname === "youtube.com") {
      const parts = url.pathname.split("/").filter(Boolean);
      if (url.pathname.replace(/\/$/, "") === "/watch") videoId = url.searchParams.get("v") || "";
      else if (["embed", "shorts", "live"].includes(parts[0])) videoId = parts[1] || "";
    }
    return YOUTUBE_ID_PATTERN.test(videoId) ? videoId : "";
  } catch {
    return "";
  }
}

export function parseCsv(text) {
  const input = String(text ?? "").replace(/^\uFEFF/, "");
  const records = [];
  let record = [];
  let field = "";
  let quoted = false;

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];
    if (quoted) {
      if (character === '"' && input[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (character === '"') quoted = false;
      else field += character;
    } else if (character === '"') quoted = true;
    else if (character === ",") {
      record.push(field);
      field = "";
    } else if (character === "\n") {
      record.push(field.replace(/\r$/, ""));
      records.push(record);
      record = [];
      field = "";
    } else field += character;
  }

  if (quoted) throw new Error("The CSV contains an unclosed quoted field.");
  if (field || record.length) {
    record.push(field.replace(/\r$/, ""));
    records.push(record);
  }
  const nonEmptyRecords = records.filter((values) => values.some((value) => String(value).trim()));
  if (!nonEmptyRecords.length) throw new Error("The CSV is empty.");

  const headers = nonEmptyRecords[0].map((header) => String(header).trim().toLowerCase());
  if (headers.some((header) => !header)) throw new Error("Every CSV column must have a header.");
  if (new Set(headers).size !== headers.length) throw new Error("The CSV contains duplicate headers.");
  if (!headers.includes("exercise_name")) throw new Error("The CSV must include an exercise_name header.");

  const rows = nonEmptyRecords.slice(1).map((values, index) => {
    const row = { row_number: index + 2 };
    headers.forEach((header, columnIndex) => {
      row[header] = String(values[columnIndex] ?? "").trim();
    });
    return row;
  });
  if (!rows.length) throw new Error("The CSV does not contain any exercise rows.");
  return { headers, rows };
}

function parseActive(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (["", "true", "1", "yes", "y"].includes(normalized)) return { value: true, error: "" };
  if (["false", "0", "no", "n"].includes(normalized)) return { value: false, error: "" };
  return { value: true, error: "is_active must be true/false, yes/no, or 1/0" };
}

const exactNameKey = (value) => String(value ?? "").trim().toLowerCase();
const exactValue = (value) => String(value ?? "").trim();

export function analyseImportRows(rows, existingExercises) {
  const analysed = rows.map((raw) => {
    const exerciseName = exactValue(raw.exercise_name || raw.title);
    let videoUrl = exactValue(raw.video_url);
    let videoId = exactValue(raw.youtube_video_id);
    const issues = [];
    if (!exerciseName) issues.push({ kind: "error", message: "Missing exercise_name" });
    if (videoId && !YOUTUBE_ID_PATTERN.test(videoId)) {
      issues.push({ kind: "error", message: "Invalid YouTube video ID" });
    }
    const derivedId = videoUrl ? extractYoutubeVideoId(videoUrl) : "";
    if (videoUrl && !derivedId) issues.push({ kind: "error", message: "Invalid YouTube URL" });
    if (videoUrl && derivedId && videoId && videoId !== derivedId) {
      issues.push({ kind: "error", message: "YouTube ID does not match the URL" });
    }
    if (!videoId && derivedId) videoId = derivedId;
    if (!videoUrl && YOUTUBE_ID_PATTERN.test(videoId)) videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
    const active = parseActive(raw.is_active);
    if (active.error) issues.push({ kind: "error", message: active.error });
    return {
      rowNumber: raw.row_number,
      raw,
      data: {
        exercise_name: exerciseName,
        video_url: videoUrl,
        youtube_video_id: videoId,
        condition: exactValue(raw.condition),
        phase: exactValue(raw.phase),
        level: exactValue(raw.level || raw.difficulty_level),
        sets: exactValue(raw.sets),
        reps: exactValue(raw.reps),
        hold: exactValue(raw.hold || raw.hold_time),
        frequency: exactValue(raw.frequency),
        equipment: exactValue(raw.equipment),
        playlist: exactValue(raw.playlist),
        description: exactValue(raw.description),
        instructions: exactValue(raw.instructions),
        thumbnail_url: exactValue(raw.thumbnail_url),
        safety_notes: exactValue(raw.safety_notes),
        is_active: active.value,
      },
      issues,
      existingTargetIds: [],
    };
  });

  const duplicateIndexes = [
    { label: "exercise name", value: (row) => exactNameKey(row.data.exercise_name) },
    { label: "YouTube URL", value: (row) => exactValue(row.data.video_url) },
    { label: "YouTube ID", value: (row) => exactValue(row.data.youtube_video_id) },
  ];
  duplicateIndexes.forEach(({ label, value }) => {
    const groups = new Map();
    analysed.forEach((row, index) => {
      const key = value(row);
      if (key) groups.set(key, [...(groups.get(key) || []), index]);
    });
    groups.forEach((indexes) => {
      if (indexes.length < 2) return;
      const rowNumbers = indexes.map((index) => analysed[index].rowNumber).join(", ");
      indexes.forEach((index) => analysed[index].issues.push({
        kind: "duplicate",
        message: `Duplicate ${label} in CSV rows ${rowNumbers}`,
      }));
    });
  });

  const existingIndexes = { name: new Map(), url: new Map(), id: new Map() };
  const addExisting = (map, key, id) => {
    if (key) map.set(key, [...(map.get(key) || []), id]);
  };
  (existingExercises || []).forEach((exercise) => {
    addExisting(existingIndexes.name, exactNameKey(exercise.title), exercise.id);
    addExisting(existingIndexes.url, exactValue(exercise.video_url), exercise.id);
    addExisting(
      existingIndexes.id,
      exactValue(exercise.youtube_video_id) || extractYoutubeVideoId(exercise.video_url),
      exercise.id,
    );
  });

  analysed.forEach((row) => {
    const checks = [
      ["exercise name", exactNameKey(row.data.exercise_name), existingIndexes.name],
      ["YouTube URL", exactValue(row.data.video_url), existingIndexes.url],
      ["YouTube ID", exactValue(row.data.youtube_video_id), existingIndexes.id],
    ];
    const ids = new Set();
    checks.forEach(([label, key, index]) => {
      const matches = key ? index.get(key) || [] : [];
      if (matches.length) {
        matches.forEach((id) => ids.add(id));
        row.issues.push({ kind: "existing", message: `Existing ${label} conflict` });
      }
    });
    row.existingTargetIds = [...ids];
  });
  return analysed;
}

function quoteCsvValue(value) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function createCsvTemplate() {
  const sample = {
    exercise_name: "Example exercise",
    video_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    youtube_video_id: "dQw4w9WgXcQ",
    condition: "knee_oa",
    phase: "Early",
    level: "Beginner",
    sets: "2",
    reps: "10",
    hold: "5 seconds",
    frequency: "Daily",
    equipment: "Chair",
    playlist: "Knee starter exercises",
    description: "Short exercise description",
    instructions: "Complete slowly and with control.",
    thumbnail_url: "",
    safety_notes: "Stop if symptoms worsen.",
    is_active: "true",
  };
  return `${BULK_EXERCISE_HEADERS.join(",")}\r\n${BULK_EXERCISE_HEADERS.map((header) => quoteCsvValue(sample[header])).join(",")}\r\n`;
}
